import type { MonthBlend } from './waterColumn';
import { LIGHT_SHAPE } from './data/light/shape';

/**
 * Light (M33): the sunlight reaching the sea's surface each day, and how it
 * fades with depth, for every place.
 *
 * **At the surface**, the day's photosynthetically available radiation
 * (PAR, 400 to 700 nm), mol photons m⁻² d⁻¹, clouds and all: the mean year
 * of NASA's MODIS-Aqua record (2003 to 2022), read by the day as the water
 * is (`monthBlend`, mean-preserving nodes), built by `scripts/data/light.ts`
 * and `lightBuild.ts`.
 *
 * **With depth**, from the water's clarity near the top, KdPAR over the
 * first optical depth (the depth where the light falls to 1/e of the
 * surface's): the mean year of NOAA's VIIRS record (2012 to 2025). Light
 * does not fade at one rate all the way down: the red end of PAR is gone in
 * the first meters, whatever the water, and below it the blue-green fades
 * more slowly, the more slowly the clearer the water. So the rate at depth
 * z is Lee et al.'s two-part form (Lee, Z.-P., Du, K.-P. & Arnone, R. 2005,
 * *Journal of Geophysical Research* 110: C02016),
 *
 *   K(z) = K1 + K2 / √(1 + z),
 *
 * K2 the part that dies with depth, measured against the clarity from
 * BGC-Argo floats' light profiles (`LIGHT_SHAPE`, `scripts/data/argoLight.ts`),
 * and K1 then whatever makes the light over the first optical depth fall by
 * exactly 1/e, so the satellite's clarity is honored where it is defined.
 * In the clearest water the measured K2 is negative, the light fading faster
 * below than near the top (the deep chlorophyll layer's), and in the
 * murkiest it outgrows the clarity: the line keeps the light falling with
 * depth only over the range of clarity its build states (`LIGHT_SHAPE`'s
 * note: below it the rate at the top, K1 + K2, turns negative, above it the
 * rate far down, K1), and tests/light.test.ts holds every place the game
 * reads within it. Station ALOHA's measured profiles (HOT's) check it
 * (`scripts/hindcast.ts`).
 *
 * The share is of the light just under the surface; the surface itself
 * reflects a few percent more, which nothing here takes off yet.
 */
export interface LightClimatology {
  /** Where the surface light and the clarity come from, as the readout names them. */
  parSource: string;
  kdSource: string;
  /** PAR at the surface over a day at the middle of each month, mol photons m⁻² d⁻¹, mean-preserving nodes. */
  surfaceParMol: readonly number[];
  /** KdPAR over the first optical depth at the middle of each month, m⁻¹, mean-preserving nodes. */
  kdPar: readonly number[];
}

/** A month-table's value on a day, between the two middles (as the water's, `waterColumn.ts`). */
function on(nodes: readonly number[], day: MonthBlend): number {
  const a = nodes[day.from] as number;
  const b = nodes[day.to] as number;
  return a + (b - a) * day.toward;
}

/** PAR at the surface on a day, mol photons m⁻² d⁻¹. */
export const surfaceParOn = (light: LightClimatology, day: MonthBlend): number => on(light.surfaceParMol, day);

/** KdPAR over the first optical depth on a day, m⁻¹. */
export const kdParOn = (light: LightClimatology, day: MonthBlend): number => on(light.kdPar, day);

/**
 * How the dying part grows with the clarity: K2 = k2AtClear + k2PerKd Kd1,
 * held at `k2Floor` or above where it has one (the game's: `LIGHT_SHAPE`,
 * generated from BGC-Argo; a trial one when the script measures it).
 */
export interface LightShape {
  k2AtClear: number;
  k2PerKd: number;
  k2Floor: number | null;
}

/** Lee's two parts for a clarity, m⁻¹: K2 as the shape gives it, K1 to make the first optical depth exact. */
export function lightParts(kdPar: number, shape: LightShape = LIGHT_SHAPE): { k1: number; k2: number } {
  const linear = shape.k2AtClear + shape.k2PerKd * kdPar;
  const k2 = shape.k2Floor === null ? linear : Math.max(shape.k2Floor, linear);
  const firstOpticalDepthM = 1 / kdPar;
  return { k1: kdPar * (1 - k2 * leeDepthTerm(firstOpticalDepthM)), k2 };
}

/** Lee's depth term: the integral of 1/√(1 + z) from the surface to `depthM`, m. */
export const leeDepthTerm = (depthM: number): number => 2 * (Math.sqrt(1 + depthM) - 1);

/** The light lost between the surface and `depthM`, in natural log units: the integral of K(z), K1 z + K2 `leeDepthTerm`. */
function attenuation(k1: number, k2: number, depthM: number): number {
  return k1 * depthM + k2 * leeDepthTerm(depthM);
}

/**
 * The depth, m, at which the light lost reaches `loss` (natural log units),
 * for two parts the loss grows with depth by; NaN where it does not reach it
 * within 524 km, the bracket's last doubling under a thousand kilometers (a
 * definition: deeper than any sea). The bracket starts at a meter and
 * doubles until it holds the loss, then is halved fifty times: 524 km at
 * most to under a nanometer (a definition; review M37 corrected both).
 */
export function depthOfLoss(k1: number, k2: number, loss: number): number {
  let lo = 0;
  let hi = 1;
  while (attenuation(k1, k2, hi) < loss) {
    hi *= 2;
    if (hi > 1e6) return Number.NaN;
  }
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    if (attenuation(k1, k2, mid) < loss) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** The share of the light just under the surface that reaches `depthM`, m. */
export function lightShare(kdPar: number, depthM: number, shape: LightShape = LIGHT_SHAPE): number {
  const { k1, k2 } = lightParts(kdPar, shape);
  return Math.exp(-attenuation(k1, k2, depthM));
}

/** The depth, m, at which the light falls to `share` of the surface's (0.01: the euphotic zone's floor). */
export function depthOfShare(kdPar: number, share: number, shape: LightShape = LIGHT_SHAPE): number {
  const { k1, k2 } = lightParts(kdPar, shape);
  return depthOfLoss(k1, k2, -Math.log(share));
}

/**
 * The light at local noon on a level surface, PAR, µmol photons m⁻² s⁻¹,
 * from the day's total and the day's length: the day's light taken to rise
 * and fall as a half sine from sunrise to sunset, as models of light in the
 * sea commonly take it (Kirk, J. T. O. 2011, *Light and Photosynthesis in
 * Aquatic Ecosystems*, 3rd ed.), so its peak is π/2 times its mean over the
 * daylight hours. No light where the sun does not rise.
 *
 * MEASURED by the hindcast against HOT's deck at Station ALOHA (M34, review
 * M36, `scripts/hindcast.ts`), each of its 556 whole days' own total spread
 * so: 120 µmol m⁻² s⁻¹ under the deck's hour around noon on average, RMS 209
 * (the hour's clouds); the deck's hour a median 1.09 times this (quartiles
 * 1.04 and 1.14). The low sun loses more of its light on its longer path
 * through the air than a half sine allows, so the day's light gathers toward
 * noon.
 */
export function noonParMicromol(parMolPerDay: number, dayLengthHours: number): number {
  if (dayLengthHours <= 0) return 0;
  // A mole is a million micromoles, an hour 3,600 seconds (definitions).
  return ((Math.PI / 2) * parMolPerDay * 1e6) / (dayLengthHours * 3600);
}
